import React, { useState, useEffect } from "react";
import axios from "../../utils/api";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Snackbar,
  Alert,
  IconButton,
  Box,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get("/user-details", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setUsers(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to fetch users");
        setLoading(false);
        showSnackbar("Failed to fetch users", "error");
      }
    };

    fetchUsers();
  }, []);

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const deleteUser = async (email) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this user?"
    );
    if (confirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`/user-details-delete`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: { email },
        });
        setUsers(users.filter((user) => user.email !== email));
        showSnackbar("User deleted successfully!", "success");
      } catch (err) {
        console.error("Error deleting user:", err);
        showSnackbar("Failed to delete user", "error");
      }
    }
  };

  const handleUpdateEmail = async () => {
    if (!selectedUser || !newEmail) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `/user-details-update`,
        {
          oldEmail: selectedUser.email,
          newEmail,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setUsers(
        users.map((user) =>
          user.email === selectedUser.email
            ? { ...user, email: newEmail }
            : user
        )
      );
      closeUpdateDialog();
      showSnackbar("Email updated successfully!", "success");
    } catch (err) {
      console.error("Error updating user email:", err);
      showSnackbar("Failed to update email", "error");
    }
  };

  const openUpdateDialog = (user) => {
    setSelectedUser(user);
    setNewEmail(user.email);
    setOpenDialog(true);
  };

  const closeUpdateDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setNewEmail("");
    setEmailError("");
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const encodeEmail = (email) => {
    return encodeURIComponent(btoa(email));
  };

  // Loading state UI
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          overflow: "hidden",
          background:
            "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
        }}
      >
        <CircularProgress
          size={48}
          thickness={4}
          sx={{
            color: "#6366f1",
          }}
        />
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#64748b" }}>
          Loading users...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorCard}>
          <span style={styles.errorIcon}>⚠️</span>
          <Typography variant="h5" style={styles.errorTitle}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="body1" style={styles.errorMessage}>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
            style={styles.retryButton}
          >
            <span style={styles.retryIcon}>🔄</span>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Background gradient */}
      <div style={styles.backgroundGradient}></div>
      
      {/* Main content */}
      <div style={styles.contentWrapper}>
        <Box style={styles.headerContainer}>
          <IconButton
            onClick={() => navigate(-1)}
            style={styles.backButton}
          >
            <ArrowBackIcon />
          </IconButton>
          <div style={styles.headerText}>
            <Typography variant="h4" style={styles.title}>
              User Management
            </Typography>
            <Typography variant="body1" style={styles.subtitle}>
              Manage and monitor all users in the system
            </Typography>
          </div>
          <div style={styles.statsBadge}>
            <span style={styles.statsNumber}>{users.length}</span>
            <span style={styles.statsLabel}>Total Users</span>
          </div>
        </Box>

        {users.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>👥</div>
            <Typography variant="h6" style={styles.emptyStateTitle}>
              No users found
            </Typography>
            <Typography variant="body2" style={styles.emptyStateText}>
              There are no users in the system yet
            </Typography>
          </div>
        ) : (
          <Grid container spacing={3} style={styles.gridContainer}>
            {users.map((user, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user._id}>
                <Card
                  onClick={() => navigate(`/user-details/${encodeEmail(user.email)}`)}
                  style={styles.card}
                  elevation={0}
                >
                  <div style={styles.cardGradient}></div>
                  <CardContent style={styles.cardContent}>
                    <div style={styles.userHeader}>
                      <div style={styles.userAvatar}>
                        {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                      </div>
                      <div style={styles.userInfo}>
                        <Typography variant="h6" style={styles.userName}>
                          {user.name || "Unnamed User"}
                        </Typography>
                        <Typography variant="body2" style={styles.userEmail}>
                          {user.email}
                        </Typography>
                      </div>
                    </div>
                    
                    <div style={styles.detailsGrid}>
                      <div style={styles.detailItem}>
                        <span style={styles.detailIcon}>🛒</span>
                        <div style={styles.detailContent}>
                          <span style={styles.detailLabel}>Purchase Type</span>
                          <span style={styles.detailValue}>
                            {user.purchaseType || "Not specified"}
                          </span>
                        </div>
                      </div>

                      <div style={styles.detailItem}>
                        <span style={styles.detailIcon}>⭐</span>
                        <div style={styles.detailContent}>
                          <span style={styles.detailLabel}>Subscription</span>
                          <span style={{
                            ...styles.detailValue,
                            ...styles.subscriptionBadge,
                            backgroundColor: user.subscriptionStatus === 'active' ? '#10b98120' : '#ef444420',
                            color: user.subscriptionStatus === 'active' ? '#10b981' : '#ef4444',
                          }}>
                            {user.subscriptionStatus || "Not specified"}
                          </span>
                        </div>
                      </div>

                      <div style={styles.detailItem}>
                        <span style={styles.detailIcon}>📅</span>
                        <div style={styles.detailContent}>
                          <span style={styles.detailLabel}>Created</span>
                          <span style={styles.detailValue}>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div style={styles.actionButtons}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openUpdateDialog(user);
                        }}
                        style={styles.editButton}
                      >
                        <span style={styles.buttonIcon}>✏️</span>
                        Update Email
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteUser(user.email);
                        }}
                        style={styles.deleteButton}
                      >
                        <span style={styles.buttonIcon}>🗑️</span>
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </div>

      {/* Custom Update Email Dialog */}
      {openDialog && (
        <div style={styles.dialogOverlay}>
          <div style={styles.dialogContainer}>
            <div style={styles.dialogHeader}>
              <h3 style={styles.dialogTitle}>Update Email Address</h3>
              <button onClick={closeUpdateDialog} style={styles.closeButton}>×</button>
            </div>
            <div style={styles.dialogBody}>
              <div style={styles.dialogIcon}>✉️</div>
              <p style={styles.dialogText}>
                Update email for <strong>{selectedUser?.name || selectedUser?.email}</strong>
              </p>
              <div style={styles.inputGroup}>
                <label htmlFor="newEmail" style={styles.inputLabel}>
                  New Email Address
                </label>
                <input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    setEmailError("");
                  }}
                  placeholder="Enter new email address"
                  style={{
                    ...styles.input,
                    ...(emailError ? styles.inputError : {}),
                  }}
                />
                {emailError && (
                  <div style={styles.dialogErrorMessage}>
                    <span style={styles.errorIcon}>⚠️</span>
                    {emailError}
                  </div>
                )}
              </div>
            </div>
            <div style={styles.dialogFooter}>
              <button
                onClick={closeUpdateDialog}
                style={styles.cancelButton}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEmail}
                style={{
                  ...styles.updateButton,
                  ...(newEmail === selectedUser?.email || !newEmail ? styles.updateButtonDisabled : {}),
                }}
                disabled={!newEmail || newEmail === selectedUser?.email}
              >
                Update Email
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          style={styles.snackbar}
          icon={false}
        >
          <div style={styles.snackbarContent}>
            <span style={styles.snackbarIcon}>
              {snackbarSeverity === "success" ? "✅" : "❌"}
            </span>
            {snackbarMessage}
          </div>
        </Alert>
      </Snackbar>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    position: "relative",
    background:
      "radial-gradient(1200px 600px at 10% 0%, rgba(37, 99, 235, 0.10), rgba(255,255,255,0) 60%), radial-gradient(900px 450px at 90% 15%, rgba(16, 185, 129, 0.10), rgba(255,255,255,0) 55%), #ffffff",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  backgroundGradient: {
    display: "none",
  },
  contentWrapper: {
    position: "relative",
    zIndex: 1,
    padding: "32px 24px",
    maxWidth: "1400px",
    margin: "0 auto",
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "40px",
    flexWrap: "wrap",
    gap: "20px",
  },
  backButton: {
    backgroundColor: "white",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.05)",
      boxShadow: "0 6px 16px rgba(0, 0, 0, 0.15)",
    },
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1a1a1a",
    marginBottom: "4px",
    letterSpacing: "-0.5px",
  },
  subtitle: {
    fontSize: "16px",
    color: "#666",
  },
  statsBadge: {
    backgroundColor: "white",
    padding: "12px 24px",
    borderRadius: "16px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  statsNumber: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#667eea",
    lineHeight: 1,
  },
  statsLabel: {
    fontSize: "12px",
    color: "#888",
    marginTop: "4px",
  },
  gridContainer: {
    marginTop: "8px",
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    position: "relative",
    overflow: "hidden",
    borderRadius: "20px",
    backgroundColor: "white",
    border: "1px solid rgba(102, 126, 234, 0.1)",
    transition: "all 0.3s ease",
    cursor: "pointer",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 20px 40px -12px rgba(102, 126, 234, 0.3)",
      borderColor: "rgba(102, 126, 234, 0.3)",
    },
  },
  cardGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #667eea, #764ba2)",
  },
  cardContent: {
    flexGrow: 1,
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  userHeader: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
  },
  userAvatar: {
    width: "48px",
    height: "48px",
    borderRadius: "14px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "white",
    fontSize: "20px",
    fontWeight: "600",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
  },
  userInfo: {
    flex: 1,
    overflow: "hidden",
  },
  userName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1a1a1a",
    marginBottom: "4px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  userEmail: {
    fontSize: "13px",
    color: "#888",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  detailsGrid: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    padding: "12px 0",
    borderTop: "1px solid #edf2f7",
    borderBottom: "1px solid #edf2f7",
  },
  detailItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  detailIcon: {
    fontSize: "16px",
    width: "24px",
  },
  detailContent: {
    flex: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  detailLabel: {
    fontSize: "12px",
    color: "#888",
    fontWeight: "500",
  },
  detailValue: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#333",
  },
  subscriptionBadge: {
    padding: "4px 8px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: "600",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
    marginTop: "4px",
  },
  editButton: {
    flex: 1,
    padding: "8px",
    border: "1px solid #667eea",
    color: "#667eea",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#667eea",
      color: "white",
      borderColor: "#667eea",
    },
  },
  deleteButton: {
    flex: 1,
    padding: "8px",
    border: "1px solid #ef4444",
    color: "#ef4444",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "none",
    borderRadius: "12px",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#ef4444",
      color: "white",
      borderColor: "#ef4444",
    },
  },
  buttonIcon: {
    marginRight: "4px",
    fontSize: "12px",
  },
  loadingContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
  },
  errorContainer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  errorCard: {
    backgroundColor: "white",
    padding: "40px",
    borderRadius: "24px",
    textAlign: "center",
    maxWidth: "400px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  },
  errorIcon: {
    fontSize: "48px",
    marginBottom: "20px",
    display: "block",
  },
  errorTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  },
  errorMessage: {
    fontSize: "14px",
    color: "#888",
    marginBottom: "24px",
  },
  retryButton: {
    backgroundColor: "#667eea",
    color: "white",
    padding: "12px 24px",
    borderRadius: "12px",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: "600",
    "&:hover": {
      backgroundColor: "#764ba2",
    },
  },
  retryIcon: {
    marginRight: "8px",
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: "white",
    borderRadius: "24px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  },
  emptyStateIcon: {
    fontSize: "64px",
    marginBottom: "20px",
    display: "block",
  },
  emptyStateTitle: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "8px",
  },
  emptyStateText: {
    fontSize: "14px",
    color: "#888",
  },
  dialogOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    animation: "fadeIn 0.2s ease-out",
  },
  dialogContainer: {
    backgroundColor: "white",
    borderRadius: "24px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    width: "90%",
    maxWidth: "450px",
    overflow: "hidden",
    animation: "slideUp 0.3s ease-out",
  },
  dialogHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #edf2f7",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dialogTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#1a1a1a",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    cursor: "pointer",
    color: "#888",
    padding: "0 8px",
    "&:hover": {
      color: "#333",
    },
  },
  dialogBody: {
    padding: "24px",
  },
  dialogIcon: {
    fontSize: "40px",
    textAlign: "center",
    marginBottom: "16px",
    display: "block",
  },
  dialogText: {
    fontSize: "14px",
    color: "#666",
    textAlign: "center",
    marginBottom: "20px",
  },
  inputGroup: {
    marginBottom: "16px",
  },
  inputLabel: {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#374151",
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "14px",
    border: "2px solid #e5e7eb",
    borderRadius: "12px",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
    "&:focus": {
      outline: "none",
      borderColor: "#667eea",
      boxShadow: "0 0 0 4px rgba(102, 126, 234, 0.1)",
    },
  },
  inputError: {
    borderColor: "#ef4444",
  },
  dialogErrorMessage: {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "8px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  dialogFooter: {
    padding: "20px 24px",
    borderTop: "1px solid #edf2f7",
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
  },
  cancelButton: {
    padding: "10px 20px",
    backgroundColor: "#f3f4f6",
    color: "#4b5563",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#e5e7eb",
    },
  },
  updateButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all 0.2s ease",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 18px rgba(102, 126, 234, 0.4)",
    },
  },
  updateButtonDisabled: {
    background: "#e5e7eb",
    color: "#9ca3af",
    boxShadow: "none",
    cursor: "not-allowed",
    "&:hover": {
      transform: "none",
      boxShadow: "none",
    },
  },
  snackbar: {
    borderRadius: "16px",
    backgroundColor: "white",
    color: "#333",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  snackbarContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
  },
  snackbarIcon: {
    fontSize: "18px",
  },
};

// Add global styles for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;
document.head.appendChild(styleSheet);

export default UsersList;